import React, { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '@/api/users.api';
import { useAuth } from '@/context/AuthContext';
import { MultiSelect, MultiSelectOption } from '@/components/ui/MultiSelect';

/**
 * V4.9.13 — "Tag teammates" block, shared by EVERY action modal.
 *
 * Returns a small bundle: the picker + note state, a render() for the UI,
 * and a payload()/hasContent helper the modal calls on submit. Keeping it
 * here means a modal adopts tagging in ~3 lines and they all behave
 * identically.
 *
 * Tagging is by USER ID (never by parsing @alias text), so a tag ALWAYS
 * reaches the recipient's My Tags — the permanent fix for the old glitch
 * where a multi-word alias could never be matched.
 *
 * Usage in a modal:
 *   const tag = useTagTeammates();
 *   // in mutationFn, after the primary action resolves:
 *   if (tag.hasContent) await ticketsApi.tag(ticketId, tag.payload());
 *   // in onSuccess: tag.reset();
 *   // in JSX (inside <Modal>): {tag.render()}
 */
export function useTagTeammates() {
  const { user } = useAuth();
  const [userIds, setUserIds] = useState<string[]>([]);
  const [note, setNote] = useState('');

  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    staleTime: 60_000
  });

  const options: MultiSelectOption[] = useMemo(() => {
    const rows = (usersQuery.data ?? []) as Array<{
      id: string;
      name?: string;
      alias?: string;
      active?: boolean;
      deleted_at?: string | null;
    }>;
    return rows
      // Active, non-deleted teammates only — and never yourself.
      .filter((u) => u && u.active !== false && !u.deleted_at && u.id !== user?.sub)
      .map((u) => ({
        value: u.id,
        label: u.alias ? `${u.name ?? u.alias} · @${u.alias}` : (u.name ?? 'User')
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [usersQuery.data, user?.sub]);

  const reset = useCallback(() => {
    setUserIds([]);
    setNote('');
  }, []);

  const hasContent = userIds.length > 0 || note.trim().length > 0;

  const payload = useCallback(
    () => ({ user_ids: userIds, note: note.trim() || undefined }),
    [userIds, note]
  );

  const render = () => (
    <div className="tag-teammates">
      <div className="tag-teammates-head">
        <span className="tag-teammates-icon" aria-hidden>
          🔔
        </span>
        <span className="tag-teammates-title">Tag teammates</span>
        <span className="tag-teammates-sub">Optional — they’ll get it in My Tags + an email</span>
      </div>
      <MultiSelect
        label="People"
        emptyLabel="Choose teammates…"
        options={options}
        value={userIds}
        onChange={setUserIds}
      />
      <textarea
        className="form-textarea tag-teammates-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        placeholder="Add a note for them (optional)…"
      />
    </div>
  );

  return { userIds, note, hasContent, payload, reset, render };
}
